export type Feature =
  | 'broker_sync'
  | 'trade_replay'
  | 'analytics'
  | 'prop_hq'
  | 'playbook'
  | 'reports';

const FREE_FEATURES: Feature[] = ['trade_replay'];

export type SubStatus = 'active' | 'trialing' | 'expired' | null;

/** A live 14-day trial counts as full Pro access until trial_ends_at passes. */
export function isEntitled(subscriptionStatus: SubStatus, trialEndsAt: string | null): boolean {
  if (subscriptionStatus === 'active') return true;
  if (subscriptionStatus === 'trialing') {
    return !!trialEndsAt && new Date(trialEndsAt).getTime() > Date.now();
  }
  return false;
}

export function canUse(
  feature: Feature,
  subscriptionStatus: SubStatus,
  trialEndsAt: string | null = null,
): boolean {
  if (subscriptionStatus === null) return true; // loading — don't block
  const entitled = isEntitled(subscriptionStatus, trialEndsAt);
  // "effectively expired" = hard expired, or a trial whose date has passed
  const effExpired =
    subscriptionStatus === 'expired' || (subscriptionStatus === 'trialing' && !entitled);
  if (FREE_FEATURES.includes(feature)) return !effExpired;
  return entitled;
}

export const FEATURE_META: Record<
  Feature,
  { en: string; he: string; pitch_en: string; pitch_he: string }
> = {
  broker_sync: {
    en: 'Auto-sync (TopstepX)',
    he: 'סנכרון אוטומטי (TopstepX)',
    pitch_en: 'Connect your broker and sync trades automatically — no manual entry ever again.',
    pitch_he: 'חבר את הברוקר שלך וסנכרן עסקאות אוטומטית — בלי הקלדה ידנית.',
  },
  trade_replay: {
    en: 'Trade Replay',
    he: 'Trade Replay',
    pitch_en: 'Watch your trades play out bar-by-bar on real OHLC charts. Find your edge.',
    pitch_he: "צפה בעסקאות שלך מתנגנות בר-אחרי-בר על גרפים אמיתיים. מצא את האדג'.",
  },
  analytics: {
    en: 'Analytics',
    he: 'Analytics',
    pitch_en: '9 advanced charts — equity curve, drawdown, heatmap, strategy comparison, and more.',
    pitch_he: '9 גרפים מתקדמים — עקומת הון, Drawdown, Heatmap, השוואת אסטרטגיות ועוד.',
  },
  prop_hq: {
    en: 'Prop HQ',
    he: 'Prop HQ',
    pitch_en:
      'Real-time drawdown tracking, profit targets, and account status for every prop account.',
    pitch_he: 'מעקב Drawdown, יעד רווח וסטטוס בזמן אמת לכל חשבון Prop.',
  },
  playbook: {
    en: 'Playbook',
    he: 'Playbook',
    pitch_en:
      'Build your strategy library. Track win rate, expectancy, and equity curve per setup.',
    pitch_he: 'בנה ספריית סטאפים. עקוב אחרי WR, Expectancy ועקומת הון לכל סטאפ.',
  },
  reports: {
    en: 'Reports & Export',
    he: 'דוחות וייצוא',
    pitch_en: 'Export professional PDF and CSV reports to share with mentors or funding desks.',
    pitch_he: 'ייצא דוחות PDF ו-CSV מקצועיים לשיתוף עם מנטור או מוסד מימון.',
  },
};
